import type { FC } from "react";
import { useState } from "react";

import Grid from "@/assets/icons/grid";
import List from "@/assets/icons/list";
import CreateConsultationModal from "@/components/modals/create-consultation-modal";
import CreateDocumentModal from "@/components/modals/create-document-modal";
import LastConsultations from "@/components/patient/last-consultations";
import LastDocuments from "@/components/patient/last-documents";
import PatientHistory from "@/components/patient/patient-history";
import PatientTabHeader from "@/components/patient/patient-tab-header";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Patient } from "@/types/api/patient";
import type { PatientTab } from "@/types/utils";

type PatientTabProps = {
  selectedTab: PatientTab;
  patient: Patient;
};

const PatientTabComponent: FC<PatientTabProps> = ({ selectedTab, patient }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImages, setIsImages] = useState(false);
  const [isTable, setIsTable] = useState(true);

  switch (selectedTab) {
    case "DASHBOARD":
      return (
        <div className="flex flex-col w-full gap-6">
          <PatientTabHeader title={"Prise en charge"} />
          <LastConsultations
            patient={patient}
            onClick={() => setIsModalOpen(true)}
          />
          <CreateConsultationModal
            open={isModalOpen}
            setOpen={setIsModalOpen}
            patient={patient}
          />
        </div>
      );
    case "PERIOTOOLS":
      return (
        <div className="flex flex-col w-full gap-6">
          <PatientTabHeader
            title={"Periotools"}
            buttonLabel={"Ajouter"}
            buttonOnClick={() => setIsModalOpen(true)}
            additionalSwitch={
              <div className="flex rounded-lg bg-gray-100 h-fit mt-1 p-[2px]">
                <Button
                  type="button"
                  variant="ghost"
                  size={"none"}
                  className={cn(["px-3 py-2", isTable ? "bg-grey-25" : ""])}
                  onClick={() => setIsTable(true)}
                >
                  <List />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size={"none"}
                  className={cn(["px-3 py-2", isTable ? "" : "bg-grey-25"])}
                  onClick={() => setIsTable(false)}
                >
                  <Grid />
                </Button>
              </div>
            }
            optionalSwitch={
              <div className="flex rounded-lg bg-gray-100 p-[2px]">
                <Button
                  type="button"
                  variant="ghost"
                  size={"none"}
                  className={cn([
                    "px-3 py-[3px]",
                    isImages ? "bg-grey-25 text-brand-950" : "text-grey-700",
                  ])}
                  onClick={() => setIsImages(true)}
                >
                  Images
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size={"none"}
                  className={cn([
                    "px-3 py-[3px]",
                    isImages ? "text-grey-700" : "bg-grey-25 text-brand-950",
                  ])}
                  onClick={() => setIsImages(false)}
                >
                  Fichiers
                </Button>
              </div>
            }
          />
          <LastDocuments
            patient={patient}
            isImages={isImages}
            isTable={isTable}
            isPeriotools={false}
          />
          <CreateDocumentModal
            open={isModalOpen}
            setOpen={setIsModalOpen}
            patient={patient}
            isPeriotools={false}
          />
        </div>
      );
    case "DOCUMENTS":
      return (
        <div className="flex flex-col w-full gap-6">
          <PatientTabHeader
            title={"Documents"}
            buttonLabel={"Ajouter"}
            buttonOnClick={() => setIsModalOpen(true)}
            additionalSwitch={
              <div className="flex rounded-lg bg-gray-100 h-fit mt-1 p-[2px]">
                <Button
                  type="button"
                  variant="ghost"
                  size={"none"}
                  className={cn(["px-3 py-2", isTable ? "bg-grey-25" : ""])}
                  onClick={() => setIsTable(true)}
                >
                  <List />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size={"none"}
                  className={cn(["px-3 py-2", isTable ? "" : "bg-grey-25"])}
                  onClick={() => setIsTable(false)}
                >
                  <Grid />
                </Button>
              </div>
            }
            optionalSwitch={
              <div className="flex rounded-lg bg-gray-100 p-[2px]">
                <Button
                  type="button"
                  variant="ghost"
                  size={"none"}
                  className={cn([
                    "px-3 py-[3px]",
                    isImages ? "bg-grey-25 text-brand-950" : "text-grey-700",
                  ])}
                  onClick={() => setIsImages(true)}
                >
                  Images
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size={"none"}
                  className={cn([
                    "px-3 py-[3px]",
                    isImages ? "text-grey-700" : "bg-grey-25 text-brand-950",
                  ])}
                  onClick={() => setIsImages(false)}
                >
                  Fichiers
                </Button>
              </div>
            }
          />
          <LastDocuments
            patient={patient}
            isImages={isImages}
            isTable={isTable}
            isPeriotools={true}
          />
          <CreateDocumentModal
            open={isModalOpen}
            setOpen={setIsModalOpen}
            patient={patient}
            isPeriotools={true}
          />
        </div>
      );
    case "HISTORY":
      return (
        <div className="flex flex-col w-full gap-6">
          <PatientTabHeader title={"Suivi"} />
          <PatientHistory attribute={"IP"} patientId={patient.id} />
          <PatientHistory attribute={"IS"} patientId={patient.id} />
          <PatientHistory attribute={"CIGARETTES"} patientId={patient.id} />
          <PatientHistory attribute={"DIABETES"} patientId={patient.id} />
          <PatientHistory attribute={"STRESS"} patientId={patient.id} />
        </div>
      );
  }
};

export default PatientTabComponent;
